"use client";

import { apiGetGuessHoldingList } from "@/api/comments";
import { get } from "@/utils/utils";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { HolderItem } from "./holders";

interface GuessOption {
    guessNo: string;
    title: string;
    mainFlag?: number;
    yesDesc?: string;
    noDesc?: string;
}

interface UseHoldersTabResult {
    loading: boolean;
    yesHolders: HolderItem[];
    noHolders: HolderItem[];
    selectedGuessNo: string;
    handleGuessChange: ( guessNo: string ) => void;
    showGuessFilter: boolean;
    guessOptions: GuessOption[];
    activeGuess?: GuessOption;
}

export function useHoldersTab( guessList: GuessOption[] ): UseHoldersTabResult {
    const [ selectedGuessNo, setSelectedGuessNo ] = useState( "" );

    useEffect( () => {
        setSelectedGuessNo( ( current ) => {
            if ( guessList.length === 0 ) return "";
            if ( current && guessList.some( ( guess ) => guess.guessNo === current ) ) {
                return current;
            }

            const mainGuess = guessList.find( ( guess ) => guess.mainFlag === 1 );

            return mainGuess ? mainGuess.guessNo : guessList[0].guessNo;
        } );
    }, [ guessList ] );

    const holdersQuery = useQuery( {
        queryKey: [ "guessHoldingList", selectedGuessNo ],
        enabled: Boolean( selectedGuessNo ),
        queryFn: async () => {
            const [ isOk, res ] = await apiGetGuessHoldingList( { guessNo: selectedGuessNo } );
            if ( !isOk ) {
                throw new Error( get( res, "msg", "Failed to fetch holders" ) );
            }
            return get( res, "data", { yesHoldingList: [], noHoldingList: [] } );
        },
    } );

    const mapHoldingList = useMemo( () => ( list: any[], position: "Yes" | "No" ): HolderItem[] => {
        // guessOptions
        const source = Array.isArray( list ) ? list : [];
        return source.map( ( item, index ) => {
            const userId = get( item, "userId", "" );
            return {
                userId,
                id: `${position}-${selectedGuessNo}-${userId || index}-${index}`,
                rank: get( item, "no", index + 1 ),
                avatar: get( item, "userAvatar", "" ),
                username: get( item, "userName", "" ),
                position,
                contracts: get( item, "quantity", 0 ),
                pnl: get( item, "totalAmount", 0 ),
                guessNo: selectedGuessNo,
            };
        } );
    }, [ selectedGuessNo ] );

    const yesHolders = useMemo( () => mapHoldingList( get( holdersQuery.data, "yesHoldingList", [] ), "Yes" ), [
        holdersQuery.data,
        mapHoldingList,
    ] );
    const noHolders = useMemo( () => mapHoldingList( get( holdersQuery.data, "noHoldingList", [] ), "No" ), [
        holdersQuery.data,
        mapHoldingList,
    ] );

    const handleGuessChange = ( guessNo: string ) => {
        setSelectedGuessNo( guessNo );
    };

    const activeGuess = useMemo( () => guessList.find( ( guess ) => guess.guessNo === selectedGuessNo ), [
        guessList,
        selectedGuessNo,
    ] );

    return {
        loading: holdersQuery.isLoading,
        yesHolders,
        noHolders,
        selectedGuessNo,
        handleGuessChange,
        showGuessFilter: guessList.length > 1,
        guessOptions: guessList,
        activeGuess,
    };
}
